import WithLoadingSpinner from 'components/withLoadingSpinner/WithLoadingSpinner';
import useAppDispatch from 'core/hooks/useAppDispatch';
import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import ItineraryComparisonMapSection from 'components/itineraryComparisonMapSection/ItineraryComparisonMapSection';
import useUrl from 'core/hooks/useUrl';
import ItinerarySearch from 'components/itinerarySearch/ItinerarySearch';
import DesignSelect from 'components/select/DesignSelect';
import { createSearchParams, useNavigate } from 'react-router-dom';
import useItineraryDataStore from '../../core/hooks/useItineraryDataStore';
import { setCoordinates, setZoomLevel } from '../itinerary/MapSlice';
import { withAuthenticationRequired } from 'react-oidc-context';

const ItineraryComparison = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { paramValue: dateParamValue } = useUrl<string>('Date');
  const { paramValue: courierParamValue } = useUrl<number>('CourierId');
  const {
    paramValue: leftSelectedItineraryId,
    setParamValue: setLeftSelectedItineraryId,
  } = useUrl<string>('LeftItineraryId');
  const {
    paramValue: rightSelectedItineraryId,
    setParamValue: setRightSelectedItineraryId,
  } = useUrl<string>('RightItineraryId');
  const [hoveredStop, setHoveredStop] = useState<any>(null);

  const handleDateChanged = (e: any) => {
    const { value } = e.target;
    navigate({
      search: createSearchParams({
        Date: value,
        CourierId: String(courierParamValue),
      }).toString(),
    });
  };

  const handleCourierChanged = ({ value }: any) => {
    navigate({
      search: createSearchParams({
        Date: String(dateParamValue),
        CourierId: value,
      }).toString(),
    });
  };

  const {
    itineraries,
    loadingItineraries,
    refresh: refreshItineraries,
  } = useItineraryDataStore(dateParamValue, courierParamValue!);

  const handleLeftSelectedItineraryChanged = ({ value }: any) => {
    setLeftSelectedItineraryId(value);

    const firstGeocodedStop = itineraries
      ?.find((x) => x.id === value)
      ?.itineraryStops.find((x) => x.longitude !== null && x.latitude != null);

    if (firstGeocodedStop) {
      dispatch(
        setCoordinates({
          latitude: firstGeocodedStop.latitude!,
          longitude: firstGeocodedStop.longitude!,
        }),
      );
      dispatch(setZoomLevel(13));
    }
  };

  const handleRightSelectedItineraryChanged = ({ value }: any) => {
    setRightSelectedItineraryId(value);
    const firstGeocodedStop = itineraries
      ?.find((x) => x.id === value)
      ?.itineraryStops.find((x) => x.longitude !== null && x.latitude != null);

    if (firstGeocodedStop) {
      dispatch(
        setCoordinates({
          latitude: firstGeocodedStop.latitude!,
          longitude: firstGeocodedStop.longitude!,
        }),
      );
      dispatch(setZoomLevel(13));
    }
  };

  const leftItineraryOptions = itineraries.map((it) => ({
    value: it.id,
    label: `${
      it?.isCourierOrder ? 'Courier Generated' : 'System Generated'
    } - ${format(parseISO(it?.createdDate), 'HH:mm')} - ${it?.itineraryStops
      .length} Stops ${it.isLateRefill ? '- Late Refill' : ''}`,
  }));

  const rightItineraryOptions = itineraries.map((it) => ({
    value: it.id,
    label: `${
      it?.isCourierOrder ? 'Courier Generated' : 'System Generated'
    } - ${format(parseISO(it?.createdDate), 'HH:mm')} - ${it?.itineraryStops
      .length} Stops ${it.isLateRefill ? '- Late Refill' : ''}`,
  }));
  return (
    <WithLoadingSpinner isLoading={loadingItineraries}>
      <div className="flex h-full flex-grow dark:bg-design-black dark:text-white">
        <div className="mx-8 flex grow flex-col gap-3">
          <div className="w-2/5">
            <ItinerarySearch
              onRefreshClicked={refreshItineraries}
              onDateChanged={handleDateChanged}
              onCourierChange={handleCourierChanged}
            />
          </div>
          <div className="flex flex-grow flex-row">
            <div className="flex w-1/2">
              <div className="flex grow flex-col gap-3">
                <div>
                  {courierParamValue && dateParamValue && (
                    <DesignSelect
                      loading={loadingItineraries}
                      options={leftItineraryOptions}
                      onChange={handleLeftSelectedItineraryChanged}
                      value={
                        leftItineraryOptions.find(
                          (it) => it.value === leftSelectedItineraryId,
                        ) ?? null
                      }
                    />
                  )}
                </div>
                <ItineraryComparisonMapSection
                  itinerary={itineraries.find(
                    (it) => it.id === leftSelectedItineraryId,
                  )}
                  events={[]}
                  mapId="left"
                  hoveredStop={hoveredStop}
                  setHoveredState={setHoveredStop}
                />
              </div>
            </div>
            <div className="ml-2 flex w-1/2">
              <div className="flex grow flex-col gap-3">
                <div>
                  {courierParamValue && dateParamValue && (
                    <DesignSelect
                      loading={loadingItineraries}
                      options={rightItineraryOptions}
                      onChange={handleRightSelectedItineraryChanged}
                      value={
                        rightItineraryOptions.find(
                          (it) => it.value === rightSelectedItineraryId,
                        ) ?? null
                      }
                    />
                  )}
                </div>
                <ItineraryComparisonMapSection
                  itinerary={itineraries.find(
                    (it) => it.id === rightSelectedItineraryId,
                  )}
                  events={[]}
                  mapId="right"
                  hoveredStop={hoveredStop}
                  setHoveredState={setHoveredStop}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </WithLoadingSpinner>
  );
};

export default withAuthenticationRequired(ItineraryComparison);
